/* eslint-disable no-restricted-imports */
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { Receipt as ReceiptIcon } from '@material-ui/icons';

import { Skeleton } from '@mui/material';
import { FormatMoney } from 'components';
import { CustomerSearchAsync, PageHeader, QuantityControl } from 'components/Contigo';
import { contigoService } from 'services';

import { useStyles } from './styles';

export default function InvoiceOrder() {
  const classes = useStyles();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [availableOrders, setAvailableOrders] = useState([]);

  const [selectedOrders, setSelectedOrders] = useState([]);

  const [invoiceCustomer, setInvoiceCustomer] = useState(null);

  const [notes, setNotes] = useState('');
  const [generateXml, setGenerateXml] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await contigoService.getOrdersAvailableForInvoicing();
      const ordersData = response.data || [];

      const mappedOrders = ordersData.map((order) => ({
        ...order,
        customer_name: order.customer?.NOME || 'Cliente não informado',
        items: (order.items || []).map((item) => ({
          ...item,
          fulfilled_quantity: item.quantity_fulfilled || 0,
          invoiced_quantity: item.quantity_invoiced || 0,
        })),
      }));

      setAvailableOrders(mappedOrders);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar pedidos disponíveis');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrder = (order) => {
    const existingIndex = selectedOrders.findIndex((o) => o.order_id === order.id);

    let updatedOrders;
    if (existingIndex >= 0) {
      updatedOrders = selectedOrders.filter((o) => o.order_id !== order.id);
      setSelectedOrders(updatedOrders);
    } else {
      const orderItems = order.items
        .filter((item) => item.fulfilled_quantity > item.invoiced_quantity)
        .map((item) => ({
          order_item_id: item.id,
          part_number: item.part_number,
          description: item.description,
          quantity_to_invoice: item.fulfilled_quantity - item.invoiced_quantity,
          max_quantity: item.fulfilled_quantity - item.invoiced_quantity,
          unit_price: item.sell_price,
        }));

      updatedOrders = [
        ...selectedOrders,
        {
          order_id: order.id,
          order_number: order.order_number,
          customer_name: order.customer_name,
          items: orderItems,
        },
      ];
      setSelectedOrders(updatedOrders);
    }

    if (updatedOrders.length > 0) {
      const orderNumbers = updatedOrders.map((o) => o.order_number).join(', ');
      setNotes(`Referente ao pedido Contigo: ${orderNumbers}`);
    } else {
      setNotes('');
    }
  };

  const isOrderSelected = (orderId) => {
    return selectedOrders.some((o) => o.order_id === orderId);
  };

  const handleQuantityChange = (orderId, itemId, newQuantity) => {
    setSelectedOrders((prev) =>
      prev.map((order) => {
        if (order.order_id === orderId) {
          return {
            ...order,
            items: order.items.map((item) => {
              if (item.order_item_id === itemId) {
                const quantity = Math.max(0, Math.min(newQuantity, item.max_quantity));
                return { ...item, quantity_to_invoice: quantity };
              }
              return item;
            }),
          };
        }
        return order;
      }),
    );
  };

  const handleDescriptionChange = (orderId, itemId, newDescription) => {
    setSelectedOrders((prev) =>
      prev.map((order) => {
        if (order.order_id === orderId) {
          return {
            ...order,
            items: order.items.map((item) => {
              if (item.order_item_id === itemId) {
                return { ...item, description: newDescription };
              }
              return item;
            }),
          };
        }
        return order;
      }),
    );
  };

  const handleIncrement = (orderId, itemId) => {
    const order = selectedOrders.find((o) => o.order_id === orderId);
    const item = order?.items.find((i) => i.order_item_id === itemId);
    if (item && item.quantity_to_invoice < item.max_quantity) {
      handleQuantityChange(orderId, itemId, item.quantity_to_invoice + 1);
    }
  };

  const handleDecrement = (orderId, itemId) => {
    const order = selectedOrders.find((o) => o.order_id === orderId);
    const item = order?.items.find((i) => i.order_item_id === itemId);
    if (item && item.quantity_to_invoice > 0) {
      handleQuantityChange(orderId, itemId, item.quantity_to_invoice - 1);
    }
  };

  const calculateTotal = () => {
    return selectedOrders.reduce((total, order) => {
      return (
        total +
        order.items.reduce((sum, item) => sum + item.quantity_to_invoice * item.unit_price, 0)
      );
    }, 0);
  };

  const getTotalItems = () => {
    return selectedOrders.reduce((total, order) => {
      return total + order.items.reduce((sum, item) => sum + item.quantity_to_invoice, 0);
    }, 0);
  };

  const handleGenerateInvoice = async () => {
    if (selectedOrders.length === 0) {
      toast.warning('Selecione pelo menos um pedido para faturar');
      return;
    }

    const hasItemsToInvoice = selectedOrders.some((order) =>
      order.items.some((item) => item.quantity_to_invoice > 0),
    );

    if (!hasItemsToInvoice) {
      toast.warning('Selecione pelo menos um item para faturar');
      return;
    }

    if (!invoiceCustomer) {
      toast.warning('Selecione o cliente da Nota Fiscal');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        customer_id: invoiceCustomer.CODIGO,
        items: selectedOrders
          .filter((order) => order.items.some((item) => item.quantity_to_invoice > 0))
          .flatMap((order) =>
            order.items
              .filter((item) => item.quantity_to_invoice > 0)
              .map((item) => ({
                order_id: order.order_id,
                order_item_id: item.order_item_id,
                quantity: item.quantity_to_invoice,
                description: item.description,
              })),
          ),
        notes,
        generate_xml: generateXml,
      };

      const response = await contigoService.createInvoice(payload);
      const invoice = response.data;

      toast.success(`Nota fiscal ${invoice.invoice_number} gerada com sucesso!`);
      history.push('/contigo/orders');
    } catch (error) {
      console.error('Erro ao gerar nota fiscal:', error);
      const errorMessage =
        error.response?.data?.message || 'Erro ao gerar nota fiscal. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    history.push('/contigo/orders');
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Skeleton variant='text' width='40%' height={40} style={{ marginBottom: 8 }} />
        <Skeleton variant='text' width='25%' height={24} style={{ marginBottom: 24 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Skeleton variant='text' width='50%' height={32} />
                <Skeleton variant='text' width='70%' height={20} style={{ marginBottom: 16 }} />
                <Skeleton variant='rectangular' height={300} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Skeleton variant='text' width='60%' height={24} />
                <Skeleton variant='rectangular' height={200} style={{ marginTop: 16 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (availableOrders.length === 0) {
    return (
      <Box className={classes.container}>
        <PageHeader onBack={handleCancel} title='Faturar Pedidos' />
        <Card style={{ marginTop: 24 }}>
          <CardContent>
            <Typography variant='h6' align='center' color='textSecondary'>
              Não há pedidos disponíveis para faturamento
            </Typography>
            <Typography
              variant='body2'
              align='center'
              color='textSecondary'
              style={{ marginTop: 8 }}
            >
              Os pedidos precisam ter itens atendidos para serem faturados.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const totalValue = calculateTotal();
  const totalItems = getTotalItems();

  return (
    <Box className={classes.container}>
      <PageHeader
        onBack={handleCancel}
        title='Faturar Pedidos'
        subtitle='Selecione os pedidos e itens para gerar a nota fiscal'
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card className={classes.mainCard}>
            <CardContent>
              <Typography variant='h6' className={classes.sectionTitle}>
                Selecione os Pedidos
              </Typography>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Marque os pedidos que deseja incluir na nota fiscal
              </Typography>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox'></TableCell>
                      <TableCell>Pedido</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell align='center'>Itens Disponíveis</TableCell>
                      <TableCell align='right'>Valor Disponível</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableOrders.map((order) => {
                      const availableValue = order.items.reduce((sum, item) => {
                        const availableQty = item.fulfilled_quantity - item.invoiced_quantity;
                        return sum + availableQty * item.sell_price;
                      }, 0);

                      const availableItemsCount = order.items.filter(
                        (item) => item.fulfilled_quantity > item.invoiced_quantity,
                      ).length;

                      return (
                        <TableRow key={order.id} hover>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              checked={isOrderSelected(order.id)}
                              onChange={() => handleToggleOrder(order)}
                              color='primary'
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant='body2' className={classes.boldText}>
                              {order.order_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant='body2'>{order.customer_name}</Typography>
                          </TableCell>
                          <TableCell align='center'>
                            <Chip label={availableItemsCount} size='small' color='primary' />
                          </TableCell>
                          <TableCell align='right'>
                            <Typography className={classes.boldText}>
                              <FormatMoney>{availableValue}</FormatMoney>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {selectedOrders.map((order) => (
            <Card key={order.order_id} className={classes.orderItemsCard} style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant='h6' className={classes.sectionTitle}>
                  {order.order_number} - {order.customer_name}
                </Typography>

                <TableContainer component={Paper} className={classes.tableContainer}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Produto</TableCell>
                        <TableCell width='30%'>Descrição (editável)</TableCell>
                        <TableCell align='center'>Disponível</TableCell>
                        <TableCell align='center'>Quantidade</TableCell>
                        <TableCell align='right'>Preço Un.</TableCell>
                        <TableCell align='right'>Subtotal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.order_item_id}>
                          <TableCell>
                            <Typography variant='body2' className={classes.boldText}>
                              {item.part_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant='outlined'
                              size='small'
                              multiline
                              rows={2}
                              value={item.description}
                              onChange={(e) =>
                                handleDescriptionChange(
                                  order.order_id,
                                  item.order_item_id,
                                  e.target.value,
                                )
                              }
                              helperText='Descrição que será enviada ao Tiny'
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <Chip
                              label={item.max_quantity}
                              size='small'
                              style={{
                                backgroundColor: '#E8F5E9',
                                color: '#2E7D32',
                              }}
                            />
                          </TableCell>
                          <TableCell align='center'>
                            <QuantityControl
                              value={item.quantity_to_invoice}
                              onChange={(newValue) =>
                                handleQuantityChange(order.order_id, item.order_item_id, newValue)
                              }
                              min={0}
                              max={item.max_quantity}
                            />
                          </TableCell>
                          <TableCell align='right'>
                            <FormatMoney>{item.unit_price}</FormatMoney>
                          </TableCell>
                          <TableCell align='right'>
                            <Typography className={classes.subtotalValue}>
                              <FormatMoney>
                                {item.quantity_to_invoice * item.unit_price}
                              </FormatMoney>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}

          {selectedOrders.length > 0 && (
            <Card className={classes.mainCard} style={{ marginTop: 16 }}>
              <CardContent>
                <Typography variant='h6' className={classes.sectionTitle}>
                  Cliente da Nota Fiscal
                </Typography>
                <Typography variant='body2' color='textSecondary' gutterBottom>
                  Selecione o cliente que aparecerá na NF
                </Typography>

                <CustomerSearchAsync
                  value={invoiceCustomer}
                  onChange={setInvoiceCustomer}
                  label='Cliente da Nota Fiscal *'
                  placeholder='Digite o nome ou CNPJ do cliente da NF'
                />

                <Divider className={classes.divider} />

                <Typography variant='subtitle1' className={classes.sectionTitle}>
                  Observações da Nota Fiscal
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  placeholder='Adicione observações (opcional)'
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />

                <Box className={classes.checkboxContainer}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={generateXml}
                        onChange={(e) => setGenerateXml(e.target.checked)}
                        color='primary'
                      />
                    }
                    label='Gerar XML da NFe'
                  />
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card className={classes.summaryCard}>
            <CardContent>
              <Typography variant='subtitle1' className={classes.sideCardTitle}>
                Resumo da Nota Fiscal
              </Typography>
              <Divider className={classes.divider} />

              <Box className={classes.summaryItem}>
                <Typography variant='body2' color='textSecondary'>
                  Pedidos Selecionados
                </Typography>
                <Chip label={selectedOrders.length} color='primary' />
              </Box>

              <Box className={classes.summaryItem}>
                <Typography variant='body2' color='textSecondary'>
                  Total de Itens
                </Typography>
                <Chip label={totalItems} color='primary' />
              </Box>

              <Box className={classes.summaryItem}>
                <Typography variant='body2' color='textSecondary'>
                  Valor Total
                </Typography>
                <Typography variant='h5' className={classes.totalValue}>
                  <FormatMoney>{totalValue}</FormatMoney>
                </Typography>
              </Box>

              {generateXml && (
                <Box className={classes.infoBox}>
                  <Typography variant='caption' color='textSecondary'>
                    ✓ XML da NFe será gerado
                  </Typography>
                </Box>
              )}

              {!invoiceCustomer && selectedOrders.length > 0 && (
                <Box className={classes.alertBox}>
                  <Typography variant='body2' className={classes.alertText}>
                    ⚠️ Selecione o cliente da NF
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card className={classes.sideCard}>
            <CardContent>
              <Typography variant='subtitle1' className={classes.sideCardTitle}>
                Instruções
              </Typography>
              <Divider className={classes.divider} />
              <Box className={classes.instructions}>
                <Typography variant='body2' color='textSecondary' paragraph>
                  1. Selecione os pedidos que deseja faturar
                </Typography>
                <Typography variant='body2' color='textSecondary' paragraph>
                  2. Ajuste as quantidades de cada item
                </Typography>
                <Typography variant='body2' color='textSecondary' paragraph>
                  3. Selecione o cliente da Nota Fiscal
                </Typography>
                <Typography variant='body2' color='textSecondary'>
                  4. A NF será registrada em todos os pedidos
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card className={classes.actionsCard}>
        <CardContent>
          <Box className={classes.actionsContainer}>
            <Button onClick={handleCancel} className={classes.cancelButton}>
              Cancelar
            </Button>
            <Button
              variant='contained'
              color='primary'
              startIcon={<ReceiptIcon />}
              onClick={handleGenerateInvoice}
              disabled={saving || selectedOrders.length === 0 || !invoiceCustomer}
              className={classes.generateButton}
            >
              {saving
                ? 'Gerando...'
                : `Gerar Nota Fiscal - ${Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(totalValue)}`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Backdrop open={saving} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </Box>
  );
}
